import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import {
  getPlans,
  getSubscriptions,
  getSubscription,
  getSubscriptionUsage,
  getSubscriptionTransactions,
  cancelSubscription,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getDateLocale } from '../utils/dateLocale';
import AddonsSection from '../components/AddonsSection';
import HelpIcon from '../components/help/HelpIcon';
import './SubscriptionPage.css';

function SubscriptionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Handle MercadoPago return and navigation state success
  useEffect(() => {
    const mpStatus = searchParams.get('status');
    if (mpStatus === 'approved' || mpStatus === 'authorized') {
      setSuccessMessage(t('subscription.paymentApproved'));
      setSearchParams({}, { replace: true });
    } else if (mpStatus === 'pending') {
      setSuccessMessage(t('subscription.paymentPending'));
      setSearchParams({}, { replace: true });
    } else if (mpStatus === 'failure' || mpStatus === 'rejected') {
      setSuccessMessage(t('subscription.paymentFailed'));
      setSearchParams({}, { replace: true });
    } else if (location.state?.success) {
      setSuccessMessage(t('subscription.planUpdated'));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [searchParams, setSearchParams, location, navigate, t]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load plans
      const plansResponse = await getPlans();
      setPlans(plansResponse.plans || []);

      // Load current subscription
      if (organization?.organizationId) {
        const subsResponse = await getSubscriptions({
          organizationId: organization.organizationId,
        });
        const activeSub = (subsResponse.subscriptions || []).find(
          (sub) => sub.status === 'active' || sub.status === 'trial'
        );

        if (activeSub) {
          setCurrentSubscription(activeSub);

          // Load usage data
          const usageResponse = await getSubscriptionUsage(activeSub.id);
          setUsage(usageResponse);

          // Load full subscription to get limits (needed to detect active add-ons)
          try {
            const detail = await getSubscription(activeSub.subscriptionId);
            setSubscriptionLimits(detail.subscription?.limits || {});
          } catch (detailErr) {
            console.error('Error loading subscription detail:', detailErr);
          }

          // Load transactions
          loadTransactions(activeSub.subscriptionId);
        }
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (subscriptionId) => {
    try {
      setLoadingTransactions(true);
      const transactionsResponse = await getSubscriptionTransactions(subscriptionId, { limit: 10 });
      setTransactions(transactionsResponse.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription(currentSubscription.id, false);
      setCancelConfirmOpen(false);
      loadData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert(t('subscription.errorCanceling'));
    }
  };

  const getPlanDetails = (planId) => {
    return plans.find((p) => p.planId === planId || p.id === planId) || {};
  };

  const getProgressColor = (percentage) => {
    if (percentage < 70) return 'success';
    if (percentage < 90) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  const currentPlan = currentSubscription ? getPlanDetails(currentSubscription.planId) : null;

  const getTrialDaysRemaining = () => {
    if (!currentSubscription?.trialEnd || currentSubscription.status !== 'trial') return null;
    const now = new Date();
    const trialEnd = new Date(currentSubscription.trialEnd);
    const diffMs = trialEnd - now;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const showTrialWarning = trialDaysRemaining !== null && trialDaysRemaining <= 7;
  const trialUrgent = trialDaysRemaining !== null && trialDaysRemaining <= 1;

  const isExpired = (() => {
    if (!currentSubscription) return false;
    if (currentSubscription.isExpired) return true;
    if (currentSubscription.currentPeriodEnd) {
      return new Date(currentSubscription.currentPeriodEnd) < new Date();
    }
    return false;
  })();

  return (
    <div className="subscription-page">
      <div className="page-header">
        <div>
          <h1>
            {t('subscription.title')} <HelpIcon topicKey="subscription.overview" />
          </h1>
          <p className="subtitle">{t('subscription.subtitle')}</p>
        </div>
      </div>

      {successMessage && (
        <div
          className={`subscription-toast ${successMessage === t('subscription.paymentFailed') ? 'subscription-toast--error' : 'subscription-toast--success'}`}
          role="status"
        >
          <span>{successMessage}</span>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setSuccessMessage(null)}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>
      )}

      {showTrialWarning && (
        <div
          className={`trial-expiry-banner ${trialUrgent ? 'trial-expiry-urgent' : ''}`}
          role="alert"
        >
          <span className="trial-expiry-message">
            {trialDaysRemaining <= 0
              ? t('subscription.trialExpiringToday')
              : t('subscription.trialExpiringDays', { count: trialDaysRemaining })}
          </span>
          <button
            type="button"
            className="btn btn-primary trial-expiry-cta"
            onClick={() => navigate('/plan-selection', { state: { currentSubscription } })}
          >
            {t('subscription.upgradeNow')}
          </button>
        </div>
      )}

      {isExpired && (
        <div className="expired-banner" role="alert">
          <span className="expired-banner-message">{t('subscription.expiredMessage')}</span>
          <button
            type="button"
            className="btn btn-primary expired-banner-cta"
            onClick={() => navigate('/plan-selection', { state: { currentSubscription } })}
          >
            {t('subscription.renewNow')}
          </button>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="current-plan-section">
        <div className="card plan-card">
          <div className="plan-card-header">
            <div>
              <h2>
                {t('subscription.currentPlan')} <HelpIcon topicKey="subscription.tiers" />
              </h2>
              {currentSubscription?.status === 'trial' && (
                <span className="badge badge-trial" role="status">
                  ⏳ {t('subscription.trial')}
                </span>
              )}
              {currentSubscription?.cancelAtPeriodEnd && (
                <span className="badge badge-warning">
                  {t('subscription.cancelingAtPeriodEnd')}
                </span>
              )}
              {isExpired && (
                <span className="badge badge-expired" role="status">
                  {t('subscription.expired')}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/plan-selection', { state: { currentSubscription } })}
            >
              {t('subscription.changePlan')}
            </button>
          </div>

          {currentPlan ? (
            <div className="plan-details">
              <div className="plan-name-section">
                <h3 className="plan-name">
                  {t(
                    `planSelection.plans.${currentPlan.planId || currentPlan.id}.name`,
                    currentPlan.displayName || currentPlan.name
                  )}
                </h3>
                {(t(`planSelection.plans.${currentPlan.planId || currentPlan.id}.badge`, '') ||
                  currentPlan.badge) && (
                  <span className="plan-badge">
                    {t(
                      `planSelection.plans.${currentPlan.planId || currentPlan.id}.badge`,
                      currentPlan.badge || ''
                    )}
                  </span>
                )}
              </div>

              <p className="plan-description">
                {t(
                  `planSelection.plans.${currentPlan.planId || currentPlan.id}.description`,
                  currentPlan.description
                )}
              </p>

              {currentPlan.pricing && (
                <div className="plan-pricing">
                  <span className="price-amount">${currentPlan.pricing.USD?.monthly || 0}</span>
                  <span className="price-period">/{t('subscription.month')}</span>
                </div>
              )}

              <div className="plan-period-info">
                <p>
                  <strong>{t('subscription.periodStart')}:</strong>{' '}
                  {new Date(currentSubscription.currentPeriodStart).toLocaleDateString(
                    getDateLocale(i18n.language),
                    {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }
                  )}
                </p>
                <p>
                  <strong>{t('subscription.periodEnd')}:</strong>{' '}
                  {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString(
                    getDateLocale(i18n.language),
                    {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }
                  )}
                </p>
                {currentSubscription.trialEnd && (
                  <p className="trial-info">
                    <strong>{t('subscription.trialEnds')}:</strong>{' '}
                    {new Date(currentSubscription.trialEnd).toLocaleDateString(
                      getDateLocale(i18n.language),
                      {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      }
                    )}
                  </p>
                )}
              </div>

              {!currentSubscription.cancelAtPeriodEnd && currentPlan.id !== 'free' && (
                <button
                  type="button"
                  className="btn btn-secondary cancel-subscription-btn"
                  onClick={() => setCancelConfirmOpen(true)}
                >
                  {t('subscription.cancelSubscription')}
                </button>
              )}
            </div>
          ) : (
            <div className="no-subscription">
              <p>{t('subscription.noSubscription')}</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/plan-selection')}
              >
                {t('subscription.choosePlan')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Available Add-ons */}
      {currentSubscription && (
        <div className="addons-page-section">
          <AddonsSection
            subscriptionId={currentSubscription.subscriptionId}
            currentPlanId={currentSubscription.planId}
            currency="USD"
            subscriptionLimits={subscriptionLimits}
            variant="inline"
            onAddonChange={loadData}
          />
        </div>
      )}

      {/* Usage Statistics */}
      {usage && currentSubscription && (
        <div className="usage-section">
          <h2>
            {t('subscription.usage')} <HelpIcon topicKey="subscription.limits" />
          </h2>
          <div className="usage-cards">
            {/* Tables Usage */}
            <div className="card usage-card">
              <div className="usage-header">
                <span className="usage-icon">🍽️</span>
                <h3>{t('subscription.tables')}</h3>
              </div>
              <div className="usage-stats">
                <div className="usage-numbers">
                  <span className="current-usage">{usage.usage?.tables || 0}</span>
                  <span className="usage-separator">/</span>
                  <span className="max-usage">
                    {usage.limits?.maxTables === -1
                      ? t('subscription.unlimited')
                      : usage.limits?.maxTables || 0}
                  </span>
                </div>
                {usage.limits?.maxTables !== -1 && (
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(usage.percentage?.tables || 0)}`}
                      style={{ width: `${usage.percentage?.tables || 0}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>

            {/* Menu Items Usage */}
            <div className="card usage-card">
              <div className="usage-header">
                <span className="usage-icon">📋</span>
                <h3>{t('subscription.menuItems')}</h3>
              </div>
              <div className="usage-stats">
                <div className="usage-numbers">
                  <span className="current-usage">{usage.usage?.menuItems || 0}</span>
                  <span className="usage-separator">/</span>
                  <span className="max-usage">
                    {usage.limits?.maxMenuItems === -1
                      ? t('subscription.unlimited')
                      : usage.limits?.maxMenuItems || 0}
                  </span>
                </div>
                {usage.limits?.maxMenuItems !== -1 && (
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(usage.percentage?.menuItems || 0)}`}
                      style={{ width: `${usage.percentage?.menuItems || 0}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>

            {/* Orders Usage */}
            <div className="card usage-card">
              <div className="usage-header">
                <span className="usage-icon">📦</span>
                <h3>{t('subscription.orders')}</h3>
              </div>
              <div className="usage-stats">
                <div className="usage-numbers">
                  <span className="current-usage">{usage.usage?.orders || 0}</span>
                  <span className="usage-separator">/</span>
                  <span className="max-usage">
                    {usage.limits?.maxMonthlyOrders === -1
                      ? t('subscription.unlimited')
                      : usage.limits?.maxMonthlyOrders || 0}
                  </span>
                </div>
                {usage.limits?.maxMonthlyOrders !== -1 && (
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(usage.percentage?.orders || 0)}`}
                      style={{ width: `${usage.percentage?.orders || 0}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>

            {/* Users Usage */}
            <div className="card usage-card">
              <div className="usage-header">
                <span className="usage-icon">👥</span>
                <h3>{t('subscription.users')}</h3>
              </div>
              <div className="usage-stats">
                <div className="usage-numbers">
                  <span className="current-usage">{usage.usage?.users || 0}</span>
                  <span className="usage-separator">/</span>
                  <span className="max-usage">
                    {usage.limits?.maxUsers === -1
                      ? t('subscription.unlimited')
                      : usage.limits?.maxUsers || 0}
                  </span>
                </div>
                {usage.limits?.maxUsers !== -1 && (
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(usage.percentage?.users || 0)}`}
                      style={{ width: `${usage.percentage?.users || 0}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {usage.warnings && usage.warnings.length > 0 && (
            <div className="usage-warnings">
              <h3>{t('subscription.warnings')}</h3>
              {usage.warnings.map((warning, index) => (
                <div key={index} className="warning-item">
                  ⚠️ {warning}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transaction History */}
      {currentSubscription && (
        <div className="transactions-section">
          <h2>{t('subscription.transactionHistory')}</h2>
          <div className="card">
            {loadingTransactions ? (
              <div className="loading">{t('common.loading')}</div>
            ) : transactions.length > 0 ? (
              <div className="transactions-table-wrapper">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>{t('subscription.transactionDate')}</th>
                      <th>{t('subscription.transactionType')}</th>
                      <th>{t('subscription.transactionAmount')}</th>
                      <th>{t('subscription.transactionStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn) => (
                      <tr key={txn.transactionId}>
                        <td>{new Date(txn.createdAt).toLocaleString()}</td>
                        <td>
                          <span className="transaction-type">
                            {t(`subscription.transactionType_${txn.type}`) || txn.type}
                          </span>
                        </td>
                        <td className="transaction-amount">
                          {txn.currency} ${txn.amount.toFixed(2)}
                        </td>
                        <td>
                          <span className={`transaction-status status-${txn.status}`}>
                            {t(`subscription.transactionStatus_${txn.status}`) || txn.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-transactions">
                <Receipt size={40} />
                <p>{t('subscription.noTransactions')}</p>
                <p className="no-transactions-hint">{t('subscription.noTransactionsHint')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirmOpen && (
        <div className="modal-overlay">
          <div
            className="modal-content modal-small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-subscription-title"
          >
            <div className="modal-header">
              <h2 id="cancel-subscription-title">{t('subscription.cancelConfirmTitle')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setCancelConfirmOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>{t('subscription.cancelConfirmMessage')}</p>
              <p className="cancel-info">{t('subscription.cancelInfo')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setCancelConfirmOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleCancelSubscription}>
                {t('subscription.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPage;
